import {Fragment, useState, FC} from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogProps,
  useTheme,
  Typography,
  Grid,
  Skeleton,
  DialogContentText,
  Box,
  Tabs,
  Tab,
  Link,
  Stack,
} from "@mui/material"
import {Button} from "../../atoms"
import NotificationCard from "../NotificationCard/NotificationCard"
import type {
  Notification,
  MarkAsReadAction,
} from "../../../../types/NotificationsModel"
import SupportModal from "../../organisms/SupportModal/SupportModal"

type Props = {
  title: string
  message: string
  showDialog: boolean
  dialogProps?: DialogProps
  disabled?: boolean
  disabledClose?: boolean
  navigateButtonLabel?: string
  handleClose: () => void
  handleNavigate: () => void
  handleMarkAsRead: MarkAsReadAction
  handleMarkAllAsRead: () => void
  unreadNotifications?: number
  loadingData?: boolean
  hasError?: boolean
  notificationData?: Notification[]
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
  isLoading?: boolean
}

function CustomTabPanel(props: TabPanelProps) {
  const {children, value, index, isLoading, ...other} = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            p: 3,
            padding: "0",
            maxHeight: "438px",
            overflowY: isLoading ? "hidden " : "auto",
          }}
        >
          {children}
        </Box>
      )}
    </div>
  )
}

function PassingIndexProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  }
}

const contactUsLink = {
  notificationBellContactUsLink:
    process.env.REACT_APP_NOTIFICATIONS_CONTACT_US_LINK ||
    "%%NOTIFICATIONS_CONTACT_US_LINK%%",
}

const NotificationModal: FC<Props> = (props) => {
  const {
    title,
    showDialog,
    message,
    dialogProps,
    disabledClose,
    navigateButtonLabel = "See More",
    handleClose,
    handleNavigate,
    handleMarkAsRead,
    handleMarkAllAsRead,
    unreadNotifications,
    loadingData,
    hasError,
    notificationData,
  } = props
  const theme = useTheme()
  const [value, setValue] = useState(0)
  const [showSupportModal, setShowSupportModal] = useState<boolean>(false)

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }

  const handleOpenSupportModal = () => {
    setShowSupportModal((open) => !open)
  }

  return (
    // @ts-ignore
    <Dialog
      id="notification-modal"
      open={showDialog}
      onClose={handleClose}
      maxWidth="md"
      sx={{
        ".MuiModal-backdrop": {
          backgroundColor: "unset",
        },
        "& .MuiDialog-paper": {
          maxHeight: "554px",
          position: "fixed",
          margin: "0",
          right: "1.9375rem",
          top: "40px",
          width: "29.438rem",
          maxWidth: "29.438rem",
          borderRadius: "0.5rem",
          backgroundColor: theme.palette.common.white,
          borderTopWidth: "7px",
          borderTopColor: "#053747",
          boxShadow:
            " 0 0 2px 0 rgba(0, 0, 0, 0.12), 0 4px 8px 0 rgba(0, 0, 0, 0.14)",
          border: "solid 1px #dedede",
        },
      }}
      {...dialogProps}
    >
      <DialogTitle
        sx={{
          fontSize: "0.875rem",
          fontStyle: "normal",
          fontWeight: 600,
          lineHeight: "1.57",
          paddingLeft: "1.0625rem",
          paddingRight: "1.25rem",
          paddingTop: "0.75rem",
          paddingBottom: "0.75rem",
          color: "#242d35",
          display: "block",
        }}
      >
        {!loadingData ? (
          <Grid
            container
            sx={{
              justifyContent: "space-between",
            }}
          >
            <Grid
              component="div"
              sx={{
                display: " flex",
                columnGap: "0.5rem",
                justifyContent: "space-between",
              }}
            >
              {title}
              {unreadNotifications && unreadNotifications > 0 ? (
                <Typography
                  fontStyle="normal"
                  fontSize="12px"
                  color="#ffffff"
                  bgcolor="#226ca6"
                  sx={{
                    borderRadius: "2px",
                    paddingX: "3px",
                    fontWeight: "600",
                    height: "16px",
                    flexGrow: "0",
                    padding: "3.5px 3px",
                    position: "relative",
                    top: "3px",
                    lineHeight: "8px",
                  }}
                >
                  {unreadNotifications <= 999 ? unreadNotifications : 999}
                </Typography>
              ) : null}
            </Grid>
            <Grid component="div">
              <Button
                disabled={disabledClose}
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  border: "none",
                  backgroundColor: "white",
                  color: "#053747",
                  fontWeight:
                    unreadNotifications && unreadNotifications > 0
                      ? 600
                      : "normal",
                  lineHeight: "1.57",
                  padding: "0",
                  "&:hover": {
                    backgroundColor: "white",
                    color: "#053747",
                    textDecoration: "underline",
                  },
                }}
                onClick={() => {
                  if (unreadNotifications && unreadNotifications > 0) {
                    handleMarkAllAsRead()
                  }
                }}
              >
                Mark all as read
              </Button>
            </Grid>
          </Grid>
        ) : (
          <Skeleton variant="rounded" width={"100%"} height={22} />
        )}
      </DialogTitle>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          marginLeft: "17px",
          marginRight: "17px",
          ".MuiTabs-indicator": {backgroundColor: "#226ca6"},
        }}
      >
        <Tabs
          sx={{minHeight: "0"}}
          value={!loadingData ? value : null}
          onChange={handleChange}
        >
          <Tab
            sx={{
              alignItems: "center",
              justifyContent: "center",
              padding: "0 8px",
              paddingBottom: "11px",
              minHeight: "30px",
              textTransform: "none",
              minWidth: "34px",
              color: "#242d35 !important",
              cursor: loadingData ? "unset" : "",
              pointerEvents: loadingData ? "none" : "",
              paddingLeft: loadingData ? "0px" : "",
            }}
            label={
              !loadingData ? (
                "All" +
                (unreadNotifications &&
                unreadNotifications > 0 &&
                unreadNotifications <= 999
                  ? ` (${unreadNotifications})`
                  : unreadNotifications && unreadNotifications > 999
                  ? " (999)"
                  : "")
              ) : (
                <Skeleton variant="rounded" width={"34px"} height={16} />
              )
            }
            {...PassingIndexProps(0)}
          />
        </Tabs>
      </Box>
      <CustomTabPanel value={value} index={0} isLoading={loadingData}>
        <DialogContent
          sx={{
            padding: "0",
          }}
        >
          {loadingData ? (
            <Fragment>
              {Array(2)
                .fill(null)
                .map((_, index) => (
                  <Stack key={index}>
                    <DialogTitle
                      sx={{
                        padding: "13px 16px 4px 10px",
                      }}
                    >
                      <Skeleton variant="rounded" width={"100%"} height={22} />
                    </DialogTitle>
                    <DialogContentText>
                      {Array(3)
                        .fill(null)
                        .map((_, index) => (
                          <Fragment key={index}>
                            <Stack
                              sx={{
                                paddingX: "16px",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              <Stack
                                spacing={0.7}
                                paddingBottom={"8px"}
                                paddingTop={"8px"}
                              >
                                <Skeleton
                                  variant="rounded"
                                  width={"100%"}
                                  height={16}
                                />
                                <Skeleton
                                  variant="rounded"
                                  width={"100%"}
                                  height={20}
                                />
                                <Skeleton
                                  variant="rounded"
                                  width={"100%"}
                                  height={16}
                                />
                              </Stack>
                            </Stack>
                          </Fragment>
                        ))}
                    </DialogContentText>
                  </Stack>
                ))}
            </Fragment>
          ) : hasError || message.length ? (
            <DialogContentText
              sx={{
                marginTop: "24px",
                marginBottom: "24px",
                marginLeft: "66px",
                width: "340px",
                fontSize: "14px",
                fontFamily: "Open Sans",
                textAlign: "center",
                color: " #595958",
                lineHeight: 1.43,
              }}
            >
              {message}
              {hasError ? (
                <Link
                  href={contactUsLink.notificationBellContactUsLink}
                  underline="always"
                  sx={{
                    lineHeight: 1.14,
                    color: "#226ca6",
                  }}
                  onClick={(e) => {
                    e.preventDefault()
                    handleOpenSupportModal()
                  }}
                >
                  contact us.
                </Link>
              ) : (
                ""
              )}
              <SupportModal
                show={showSupportModal}
                handleToggle={handleOpenSupportModal}
                title="Client support"
              />
            </DialogContentText>
          ) : (
            <Fragment>
              {notificationData?.map((item, i) => (
                <Fragment key={i}>
                  <DialogTitle
                    sx={{
                      padding: "12px 16px 0px 10px",
                      fontSize: "12px",
                      fontWeight: "normal",
                      lineHeight: "1.83",
                      letterSpacing: "normal",
                      textAlign: "left",
                      color: " #595958",
                    }}
                  >
                    <Box>{item.categoryDateName}</Box>
                  </DialogTitle>
                  {item?.notificationList.map((notificationdetail, j) => (
                    <Box
                      key={j}
                      sx={{
                        position: "relative",
                        paddingLeft: "16px",
                        borderBottom: "solid 1px #eee",
                      }}
                    >
                      <DialogContentText
                        sx={{
                          height: "84px",
                          padding: "8px 0px",
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            left: 0,
                            top: "6px",
                            bottom: "3px",
                            width: "3px",
                            backgroundColor: notificationdetail.read
                              ? ""
                              : " #226ca6",
                          }}
                        ></Box>

                        <NotificationCard
                          key={notificationdetail.notificationId}
                          notificationId={notificationdetail.notificationId}
                          title={notificationdetail.projectName}
                          documentName={notificationdetail.documentDescription}
                          date={notificationdetail.createdDate}
                          documentURL={notificationdetail.link}
                          read={notificationdetail.read}
                          icon={notificationdetail.icon}
                          strictlyConfidential={
                            notificationdetail.strictlyConfidential
                          }
                          onAction={handleMarkAsRead}
                        />
                      </DialogContentText>
                    </Box>
                  ))}
                </Fragment>
              ))}
            </Fragment>
          )}
        </DialogContent>
      </CustomTabPanel>
      <DialogActions sx={{justifyContent: "center", paddingY: "4px"}}>
        {!loadingData ? (
          <Button
            disabled={disabledClose}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              border: "none",
              backgroundColor: "white",
              color: "#414141",
              height: "1.375rem",
              padding: "0",
              "&:hover": {
                backgroundColor: "white",
                color: "#053747",
                textDecoration: "underline",
              },
            }}
            onClick={() => {
              handleNavigate()
              handleClose()
            }}
          >
            {navigateButtonLabel}
          </Button>
        ) : (
          <Skeleton variant="rounded" width={"54px"} height={16} />
        )}
      </DialogActions>
    </Dialog>
  )
}

export default NotificationModal
